import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Box,
    Divider,
    Chip,
    Typography,
} from "@mui/material";
import apiClient from "../../services/apiClient";

export interface ChargeCodeDialogProps {
    open: boolean;
    isAddMode?: boolean;
    saving: boolean;
    formData: any;
    onClose: () => void;
    onSave: () => void;
    onChange: (key: string, value: any) => void;
}

export interface DropdownFieldProps {
    label: string;
    value: any;
    options: { id: any; name: string }[];
    onChange: (value: any) => void;
    multiple?: boolean;
    helperText?: string;
}
export interface TaxCode {
    id: number;
    code: string;
    name: string;
    rate: string | number;
    type: "percentage" | "fixed";
    displayName: string; // <-- add this
}




export interface LabeledTextFieldProps {
    label: string;
    value: any;
    onChange: (val: any) => void;
    helperText?: string;
    type?: string;
    disabled?: boolean;
}

export const LabeledTextField: React.FC<LabeledTextFieldProps> = ({
    label,
    value,
    onChange,
    helperText,
    type = "text",
    disabled = false,
}) => {
    const formatValue = (val: any) => {
        if (!val) return "";
        if (type === "date") {
            // keep YYYY-MM-DD
            return val instanceof Date ? val.toISOString().split("T")[0] : val.split("T")[0];
        }
        if (type === "datetime-local") {
            // keep YYYY-MM-DDTHH:mm
            if (val instanceof Date) {
                const iso = val.toISOString();
                return iso.substring(0, 16); // YYYY-MM-DDTHH:mm
            }
            return val.replace(" ", "T").substring(0, 16);
        }
        return val;
    };
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value;
        if (type === "number") val = String(Number(val));
        onChange(val);
    };
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {label}
            </Typography>
            <TextField
                type={type}
                variant="outlined"
                size="small"
                value={formatValue(value)}
                onChange={handleChange}
                helperText={helperText}
                disabled={disabled}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        minHeight: 32,
                        paddingTop: 0.5,
                        paddingBottom: 0.5,
                        borderRadius: 1.5,
                    },
                    "& .MuiInputBase-input": {
                        paddingTop: 0.5,
                        paddingBottom: 0.5,
                    },
                    "& .MuiFormHelperText-root": {
                        mt: 0.5,
                    },
                }}
            />
        </Box>
    )
};
export const DropdownField: React.FC<DropdownFieldProps> = ({
    label,
    value,
    options,
    onChange,
    multiple = false,
    helperText,
}) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {label}
            </Typography>

            <TextField
                select
                value={value || (multiple ? [] : "")}
                onChange={(e) => {
                    if (multiple) {
                        const selected = Array.isArray(e.target.value) ? e.target.value : e.target.value.split(",");

                        onChange(selected);
                    } else {
                        onChange(e.target.value);
                    }
                }}
                SelectProps={{
                    multiple,
                    renderValue: (selected: any) => {
                        if (multiple && Array.isArray(selected)) {
                            return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((val: any) => {
                                        const item = options.find((o) => o.id === val);
                                        return <Chip key={val} label={item?.name || val} size="small" />;
                                    })}
                                </Box>
                            );
                        }
                        const item = options.find((o) => o.id === selected);
                        return <span>{item?.name || selected}</span>;
                    },
                    MenuProps: { PaperProps: { sx: { maxHeight: 200 } } },
                }}
                fullWidth
                variant="outlined"
                size="small"
                helperText={helperText} // show helper text
                sx={{
                    "& .MuiOutlinedInput-root": {
                        minHeight: 32,
                        paddingTop: 0.5,
                        paddingBottom: 0.5,
                        borderRadius: 1.5,
                        "& .MuiSelect-select": {
                            paddingTop: 0.5,
                            paddingBottom: 0.5,
                        },
                    },
                    "& .MuiInputBase-input": {
                        paddingTop: 0.5,
                        paddingBottom: 0.5,
                    },
                }}
            >
                {!multiple && (
                    <MenuItem value="" disabled>
                        Select {label.toLowerCase()}
                    </MenuItem>
                )}
                {options.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );
};


const ChargeCodeDialog: React.FC<ChargeCodeDialogProps> = ({
    open,
    isAddMode = false,
    saving,
    formData,
    onClose,
    onSave,
    onChange,
}) => {

    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState<any[]>([]);
    const [providers, setProviders] = useState<{ id: number; name: string; providers: any[] }[]>([]);
    const [taxCodes, setTaxCodes] = useState<any[]>([]);
    const fetchData = async (url: string, mapFn?: (data: any) => any[]) => {
        try {
            setLoading(true);
            const response: any = await apiClient(url, { method: "GET" });
            return mapFn ? mapFn(response.data) : response.data;
        } catch (err: any) {
            console.error(err);
            return [];
        } finally {
            setLoading(false);
        }
    };
    console.log('formdata', formData);
    // Fetch providers and tax codes when dialog opens
    useEffect(() => {
        if (open) {
            // Fetch providers
            fetchData("v1/resource/service_providers/all", (data) =>
                data.service_providers?.map((p: any) => ({
                    id: p.id,
                    name: `${p.user.first_name} ${p.user.last_name}`,
                })) || []
            ).then(setProviders);

            fetchData("v1/resource/tax_codes", (data) =>
                data.tax_codes || []
            ).then(setTaxCodes);
        }
    }, [open]);

    // Filter services based on selected provider
    useEffect(() => {
        const fetchProviderServices = async () => {
            if (!formData.provider_id) {
                setServices([]);
                return;
            }
            try {
                setLoading(true);
                const response: any = await apiClient(
                    `v1/resource/services?service_provider_id=${formData.provider_id}`,
                    { method: "GET" }
                );
                const fullServices = response.data.services || [];
                setServices(fullServices);

                // If editing, patch formData.service with actual service object from fetched list
                if (!isAddMode && formData.service_id) {
                    const matched = fullServices.find((s: any) => s.id === formData.service_id);
                    if (matched) {
                        onChange("service", matched);
                    }
                }
            } catch (err) {
                console.error(err);
                setServices([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProviderServices();
    }, [formData.provider_id]);




    const durationOptions = [
        { id: 0.5, name: "30 mins" },
        { id: 1, name: "60 mins" },
        { id: 1.5, name: "90 mins" },
        { id: 2, name: "120 mins" },
        { id: 2.5, name: "150 mins" },
        { id: 3, name: "180 mins" },
        { id: 3.5, name: "210 mins" },
        { id: 4, name: "240 mins" },
        { id: 4.5, name: "270 mins" },
        { id: 5, name: "300 mins" },
        { id: 5.5, name: "330 mins" },
        { id: 6, name: "360 mins" },
    ];


    useEffect(() => {
        if (!formData.amount) return;

        let total = Number(formData.amount);
        console.log("Base amount:", total);

        if (formData.tax_code?.tax_codes && formData.tax_code?.tax_codes.length) {
            console.log("Selected tax objects:", formData.tax_code);

            formData.tax_code?.tax_codes.forEach((tax: any) => {
                if (tax.type === "percentage") {
                    const taxAmount = (total * Number(tax.rate)) / 100;
                    console.log(`Applying ${tax.rate}% tax: +${taxAmount}`);
                    total += taxAmount;
                } else if (tax.type === "fixed") {
                    console.log(`Applying fixed tax: +${tax.rate}`);
                    total += Number(tax.rate);
                }
            });
        } else {
            console.log("No tax selected.");
        }

        console.log("Final calculated amount:", total.toFixed(2));
        onChange("final_amount", Number(total.toFixed(2)));
    }, [formData.amount, formData.tax_code?.tax_codes]);

    console.log("Dropdown value (formData.service?.id):", formData);
    console.log("Dropdown options:", services.map(s => s.id));
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 2, background: "#f9fafb", position: "relative" } }}
        >
            {loading && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        bgcolor: "rgba(255,255,255,0.7)",
                        zIndex: 10,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 3,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <DialogTitle sx={{ fontWeight: 600, color: "#333", textAlign: "center" }}>
                {isAddMode ? "Add Charge Code" : "Edit Charge Code"}
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Row 1: Service Provider + Service */}
                <Box sx={{ display: "flex", gap: 2 }}>
                    <DropdownField
                        label="Service Provider"
                        value={formData.provider_id || []}
                        options={providers}

                        onChange={(val: any[]) => onChange("provider_id", val)}
                        helperText="Select one or more service providers"
                    />

                    <DropdownField
                        label="Service"
                        value={formData.service?.id || formData?.service_id || ""}
                        options={services.map(s => ({
                            id: s.id,
                            name: s.services_name || s.name,
                        }))}
                        onChange={(selected: any) => {
                            console.log("Dropdown onChange triggered");
                            console.log("Selected value from dropdown:", selected);
                            console.log("Current services list:", services);

                            // Find full object from selected ID
                            const selectedService = services.find(s => s.id === selected);
                            console.log("Mapped full service object:", selectedService);

                            // Store full object in formData
                            onChange("service", selectedService);
                            console.log("Updated formData.service:", {
                                ...formData,
                                service: selectedService,
                            });
                        }}
                        helperText="Select a service"
                    />

                </Box>


                {/* Row 2: Charge Code + Amount */}
                <Box sx={{ display: "flex", gap: 2 }}>
                    <LabeledTextField
                        label="Charge Code"
                        value={formData.charge_code}
                        onChange={(val) => onChange("charge_code", val)}
                        helperText="Unique code for the charge"
                    />


                    <LabeledTextField
                        label="Charge Code Name"
                        value={formData.name}
                        onChange={(val) => onChange("name", val)}
                        helperText="Enter the charge code name"
                    />

                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <LabeledTextField
                        label="Rate"
                        type="number"
                        value={formData.amount}
                        onChange={(val) => onChange("amount", val)}
                        helperText="Enter the service amount in ₹"
                    />
                    <DropdownField
                        label="Duration"
                        value={Number(formData.duration) || ""} // ensure number type
                        options={durationOptions}
                        onChange={(val) => {
                            const numericVal = Number(val); // convert to number
                            const selectedOption = durationOptions.find(o => o.id === numericVal);
                            console.log("Selected duration:", numericVal, "-", selectedOption?.name);
                            onChange("duration", numericVal); // store as number
                        }}
                        helperText="Select the service duration"
                    />

                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <DropdownField
                        label="Tax Code"
                        value={(formData.tax_code?.tax_codes || []).map((t: any) => t.id)}
                        options={taxCodes.map((t: any) => ({
                            id: t.id,
                            name: `${t.code} - ${t.name} (${t.rate}${t.type === 'percentage' ? '%' : '₹'}, ${t.type})`,
                        }))}
                        onChange={(selectedIds: number[] | number) => {
                            const idsArray = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
                            const selectedTaxes = taxCodes.filter(t => idsArray.includes(t.id));
                            onChange("tax_code", {
                                ...formData.tax_code,
                                tax_codes: selectedTaxes,
                            });
                        }}
                        multiple
                        helperText="Select the applicable tax codes"
                    />
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 500 }}>
                    Final Amount: ₹{formData.final_amount || 0}
                </Typography>


            </DialogContent>

            <DialogActions sx={{ justifyContent: "flex-end", mt: 1 }}>
                <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2, textTransform: "none", px: 3 }}>
                    Cancel
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    color="success"
                    disabled={saving}
                    sx={{ borderRadius: 2, textTransform: "none", px: 4 }}
                >
                    {saving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChargeCodeDialog;
